import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

// Matches the homepage's exact palette (see pages/home/HomePage.jsx):
// background #09090f, violet→indigo→blue accent gradient, subtle top glow.
const StockerLoading = ({ retryCount = 0 }) => {
  const { t } = useTranslation();
  // Rotating, on-brand loading copy — same spirit as LoginPage's LOADING_MESSAGES,
  // tuned for Stocker's credibility/evidence/sentiment positioning.
  const LOADING_PHRASES = t('common.loadingPhrases', { returnObjects: true });
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const dotsTimer = setInterval(() => setDots((d) => (d + 1) % 4), 400);
    return () => clearInterval(dotsTimer);
  }, []);

  useEffect(() => {
    const phraseTimer = setInterval(
      () => setPhraseIdx((i) => (i + 1) % LOADING_PHRASES.length),
      1800
    );
    return () => clearInterval(phraseTimer);
  }, [LOADING_PHRASES.length]);

  let connectionCopy = t('common.connectionDefault');
  if (retryCount === 1) connectionCopy = t('common.connectionRetry1');
  if (retryCount >= 2)
    connectionCopy = t('common.connectionRetry2');

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{ background: "#09090f", fontFamily: "'Inter', 'Helvetica Neue', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
        .stocker-phrase {
          background: linear-gradient(135deg, #a78bfa 0%, #818cf8 50%, #60a5fa 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: stocker-phrase-fade 1.8s ease-in-out infinite;
          letter-spacing: -0.01em;
        }
        @keyframes stocker-phrase-fade {
          0% { opacity: 0; transform: translateY(6px); }
          12% { opacity: 1; transform: translateY(0); }
          88% { opacity: 1; }
          100% { opacity: 0; }
        }
        .stocker-logo-glow {
          filter: drop-shadow(0 0 22px rgba(139, 92, 246, 0.35));
          animation: stocker-logo-pulse 2.4s ease-in-out infinite;
        }
        @keyframes stocker-logo-pulse {
          0%, 100% { filter: drop-shadow(0 0 18px rgba(139, 92, 246, 0.28)); }
          50% { filter: drop-shadow(0 0 30px rgba(139, 92, 246, 0.5)); }
        }
      `}</style>

      {/* Top radial glow — identical to the homepage hero */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: 900,
          height: 480,
          background: "radial-gradient(ellipse at 50% -10%, rgba(109,40,217,0.16) 0%, transparent 65%)",
          pointerEvents: "none",
        }}
      />

      <div className="relative flex flex-col items-center gap-8 px-6 text-center">
        {/* Source PNG is only 352x77px natively -- anything above ~h-16
            upscales past its real resolution and reads as fuzzy, especially
            with the glow filter softening edges further. Kept under that. */}
        <img
          src="/StockerLogo.png"
          alt="Stocker"
          className="stocker-logo-glow h-12 sm:h-14 select-none"
        />

        <div className="h-9 sm:h-10 flex items-center">
          <p
            key={phraseIdx}
            className="stocker-phrase text-2xl sm:text-3xl font-bold"
          >
            {LOADING_PHRASES[phraseIdx]}
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium" style={{ color: "#94a3b8" }}>
            {connectionCopy}
          </p>
          <p className="text-sm font-semibold tracking-wide" style={{ color: "#64748b" }}>
            {t('common.loadingSuffix')}{".".repeat(dots)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StockerLoading;
